import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateSourcingRequirementInput,
  OrderSourcing,
  SetSourcingStatusInput,
  SourcingRequirement,
  SourcingRequirementPage,
  UpdateSourcingRequirementInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  createSourcingRequirement,
  deleteSourcingRequirement,
  getOrderSourcing,
  searchSourcing,
  setSourcingStatus,
  updateSourcingRequirement,
  type SourcingSearchParams,
} from './sourcing-api.js';

/**
 * The sourcing hooks.
 *
 * EVERY MUTATION INVALIDATES BOTH VIEWS — the standalone list and the card on the order — because
 * the same row appears on both and they are read side by side: somebody advances a need from the
 * order screen, then opens the sourcing list to see what is left. Refreshing only the one they
 * pressed the button on leaves the other quietly out of date, and a shop that catches the two
 * disagreeing once stops trusting either.
 *
 * None of these goes through the offline write queue. A sourcing row is a working note somebody
 * makes with the material in front of them, not a payment that must survive a dropped connection,
 * and replaying a status change twenty minutes later against a row somebody else has since
 * finished is a 409 nobody asked for.
 */

export const sourcingKeys = {
  all: ['sourcing'] as const,
  search: (params: SourcingSearchParams) => ['sourcing', 'search', params] as const,
  order: (orderId: string) => ['sourcing', 'order', orderId] as const,
};

/**
 * Everything the shop still has to get.
 *
 * `enabled` exists for the one caller that must not ask at all: a tailor is refused every sourcing
 * route, and a screen that renders its refusal while still firing a request that 403s is teaching
 * the API to log an error about something nobody did wrong.
 */
export function useSourcingSearch(
  params: SourcingSearchParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<SourcingRequirementPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: sourcingKeys.search(params),
    queryFn: () => searchSourcing(client, params),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  });
}

/** One order's needs. `null` for a caller that must not ask — see above. */
export function useOrderSourcing(orderId: string | null): UseQueryResult<OrderSourcing> {
  const client = useApiClient();
  return useQuery({
    queryKey: sourcingKeys.order(orderId ?? ''),
    queryFn: () => getOrderSourcing(client, orderId as string),
    enabled: orderId !== null && orderId !== '',
  });
}

/** Both views at once. See the note at the top: the same row is read from two screens. */
function useSourcingInvalidation(): (orderId?: string) => void {
  const queryClient = useQueryClient();
  return (orderId?: string) => {
    void queryClient.invalidateQueries({ queryKey: sourcingKeys.all });
    if (orderId) void queryClient.invalidateQueries({ queryKey: sourcingKeys.order(orderId) });
  };
}

export function useCreateSourcingRequirement(): UseMutationResult<
  SourcingRequirement,
  unknown,
  CreateSourcingRequirementInput
> {
  const client = useApiClient();
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: (input: CreateSourcingRequirementInput) => createSourcingRequirement(client, input),
    onSuccess: (row) => invalidate(row.orderId),
  });
}

export function useUpdateSourcingRequirement(): UseMutationResult<
  SourcingRequirement,
  unknown,
  { sourcingId: string; patch: UpdateSourcingRequirementInput }
> {
  const client = useApiClient();
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({
      sourcingId,
      patch,
    }: {
      sourcingId: string;
      patch: UpdateSourcingRequirementInput;
    }) => updateSourcingRequirement(client, sourcingId, patch),
    onSuccess: (row) => invalidate(row.orderId),
  });
}

export function useSetSourcingStatus(): UseMutationResult<
  SourcingRequirement,
  unknown,
  { sourcingId: string; input: SetSourcingStatusInput }
> {
  const client = useApiClient();
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({ sourcingId, input }: { sourcingId: string; input: SetSourcingStatusInput }) =>
      setSourcingStatus(client, sourcingId, input),
    onSuccess: (row) => invalidate(row.orderId),
  });
}

/**
 * Removing one. The order id is carried in rather than read off the response, because a 204 has no
 * body to read it from and the card on that order still has to be refreshed.
 */
export function useDeleteSourcingRequirement(): UseMutationResult<
  void,
  unknown,
  { sourcingId: string; orderId: string }
> {
  const client = useApiClient();
  const invalidate = useSourcingInvalidation();
  return useMutation({
    mutationFn: ({ sourcingId }: { sourcingId: string; orderId: string }) =>
      deleteSourcingRequirement(client, sourcingId),
    onSuccess: (_result, variables) => invalidate(variables.orderId),
  });
}
